import { UserProfile } from 'api/account/models/UserProfile';
import { CatalogSupplierSearchCriteria } from 'api/catalog/models/CatalogSupplierSearchCriteria';
import { PeriodicityContracts } from 'api/contracts/enums/Periodicity';
import MaintenanceContractsService from 'api/contracts/maintenanceContract/MaintenanceContractsService';
import UseContractsService from 'api/contracts/useContract/UseContractsService';
import SuppliersService from 'api/suppliers/SuppliersService';
import MaintenanceContractTypesService from 'api/maintenanceContractTypes/MaintenanceContractTypesService';
import UsersService from 'api/users/UsersService';
import IconBack from 'assets/svg/Arrow-grey.svg';
import Button from 'common/components/button/Button';
import DateTimePickerRange from 'common/components/dateTimePickerRange/DateTimePickerRange';
import FileSelector from 'common/components/fileSelector/FileSelector';
import FormItem from 'common/components/formItem/FormItem';
import InputController from 'common/components/input/InputController';
import InputError from 'common/components/inputError/InputError';
import InputGroupController from 'common/components/inputGroup/InputGroupController';
import Label from 'common/components/label/Label';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Select from 'common/components/select/Select';
import SelectController from 'common/components/select/SelectController';
import CustomFile from 'common/models/CustomFile';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import Utils from 'common/services/Utils';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, DEFAULT_LANGUAGE, LOGGER_LOG_TYPE, removeAccents, STORAGE } from 'Config';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { Reducers } from 'store/types';
import { MaintenanceContractDto, MaintenanceContractTypeItemDto } from '../../../../../../api/contracts/maintenanceContract/models/MaintenanceContractDto';
import styles from './MaintenanceContractsScreen.module.scss';
import Storage from 'common/services/Storage';
import Separator from 'common/components/separator/Separator';
import ContractInvoices from '../invoices/Invoices';
import { ContractInvoiceDto } from 'api/contracts/invoices/models/InvoiceDto';
import { ReminderTriggersDto } from 'api/reminders/models/ReminderDto';
import ReminderTriggerForm from 'screens/vehicle/components/carReminderTab/components/reminderTriggerForm/ReminderTriggerForm';
import { UsersSelectItemDto } from 'api/users/models/UserDto';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import AdditionalDataService from 'api/additional-data/AdditionalDataService';
import { AdditionalDataViewModel } from 'api/additional-data/models/AdditionalDataViewModel';
import AdditionalDataForm from 'screens/additional-data/additionalDataForm/AdditionalDataForm';

export type Props = {
    itemId?: string;
    startDateEdit?: Date;
    endDateEdit?: Date;
    isDetails: boolean;
    editable: boolean;
    onBackList: () => void;
    onItemDetails: (state: boolean) => void;
    onSubmitted: () => void;
    vehicle?: VehicleDto

} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const MaintenanceContractsScreen = ({ itemId, startDateEdit, endDateEdit, isDetails, onBackList, onItemDetails, onSubmitted, vehicle, ...props }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const { vehicleId } = useParams<{ vehicleId: string }>();

    const [itemToRemove, setItemToRemove] = useState<MaintenanceContractDto>();
    const [dialogDeleteItemIsOpen, setDialogDeleteItemIsOpen] = React.useState(false);
    const [maintenanceContract, setMaintenanceContract] = useState<MaintenanceContractDto | null>(null);
    const locale = Storage.get(STORAGE.CURRENT_LOCALE) || DEFAULT_LANGUAGE;
    const [useContractOptions, setUseContractOptions] = useState<SelectValueLabel[]>([]);
    const [maintenanceContractTypeOptions, setMaintenanceContractTypeOptions] = useState<SelectValueLabel[]>([]);
    const [supplierOptions, setSupplierOptions] = useState<SelectValueLabel[]>([]);

    const [selectPeriodicity, setSelectedPeriodicity] = useState<string | null>(null);

    const periodicityOptions: SelectValueLabel[] = Object.keys(PeriodicityContracts).map(key => ({
        value: key,
        label: t('common.periodicities.' + key as any)
    }));

    const [includeContract, setIncludeContract] = useState<boolean>(false);
    const [savedFiles, setSavedFiles] = useState<CustomFile[]>([]);

    const form = useForm<MaintenanceContractDto>({ shouldUnregister: false });
    const { handleSubmit, getValues, setValue, control, errors, reset, watch } = form;

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasContractsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_CONTRACTS_WRITE']);

    const [invoiceMode, setInvoiceMode] = useState<'add' | 'list'>('list')
    const [invoicesToRemove, setInvoicesToRemove] = useState<string[]>([])
    const [reRender, setReRender] = useState<number>(1);

    // ----------------------------- REMINDERS ----------------------------------------------       
    const [criteriaOptions, setCriteriaOptions] = useState<SelectValueLabel[]>(() => {
        const opts: SelectValueLabel[] = [
            { value: 'DATE', label: t('reminders.notifications.criteria.DATE') }
        ];
        if (vehicle?.segmentFeatures?.includes('KM')) {
            opts.push({ value: 'KMS', label: t('reminders.notifications.criteria.KMS') });
        }
        if (vehicle?.segmentFeatures?.includes('HOURS')) {
            opts.push({ value: 'HOURS', label: t('reminders.notifications.criteria.HOURS') });
        }
        return opts;
    });


    const [measureOptions, setMeasureOptions] = useState<SelectValueLabel[]>([
        { value: 'HOURS', label: t('reminders.notifications.measure.HOURS') },
        { value: 'DAY', label: t('reminders.notifications.measure.DAY') },
        { value: 'YEAR', label: t('reminders.notifications.measure.YEAR') },
        { value: 'MONTH', label: t('reminders.notifications.measure.MONTH') },
        { value: 'WEEK', label: t('reminders.notifications.measure.WEEK') }]);

    const [responsablesOptions, setResponsablesOptions] = useState<SelectValueLabel[]>([]);
    // ---------------------------------------------------------------------------------------
    const [initial, setInitial] = useState<AdditionalDataViewModel[]>([]);

    const getData = async () => {
        setValue('attachmentsToRemove', []);
        try {

            Loading.show();

            const useContracts = await UseContractsService.getUseContractsByVehicle(vehicleId, true);
            setUseContractOptions(useContracts);

            // The maintenance types are loaded by the effect below, which reacts to the picked supplier.

            // A supplier that declares any maintenance type is also a valid maintenance-contract supplier.
            const suppliers = await SuppliersService.catalog({ contractTypeName: 'MAINTENANCE', hasAnyMaintenanceType: true } as CatalogSupplierSearchCriteria);
            setSupplierOptions(Utils.sortAlphabetically((suppliers || []).map((x: SelectValueLabel) => ({ value: x.value || '', label: x.label || '' }))));

            const responsables = await UsersService.getAllForCompany();
            const __responsablesOptions = (responsables || []).map((x: UsersSelectItemDto) => ({ value: x.email || '', label: x.realName ? `${x.realName} (${x.email})` : x.email || '' }));
            setResponsablesOptions(__responsablesOptions);


            if (itemId) {
                const result = await MaintenanceContractsService.getById(itemId);

                if (result === undefined) {
                    addToast(t('common.messages.error_load_info'), { appearance: 'error' });
                    Logger.error(LOGGER_LOG_TYPE.MAINTENANCE_CONTRACTS, `Couldn't get the maintenance contract with id: ${itemId}`);
                    onBackList();
                }

                result.startDate = moment(startDateEdit ?? result.startDate).toDate();
                result.endDate = moment(endDateEdit ?? result.endDate).toDate();

                if (result && result.triggers?.length > 0) {
                    result.triggers = result.triggers.map((x: ReminderTriggersDto) => {

                        const __contacts = x.contacts?.filter(z => !responsables.find((y: UsersSelectItemDto) => { return y.email === z.email }));
                        if (__contacts) {
                            __contacts.forEach(c => {
                                if (!__responsablesOptions.find((y: SelectValueLabel) => { return y.value === c.email })) {
                                    __responsablesOptions.push({ value: c.email || '', label: c.email || '' });
                                }
                            });
                        }

                        return {
                            ...x,
                            firstDate: x.firstDate ? new Date(x.firstDate) : null,
                            stopDate: x.stopDate ? new Date(x.stopDate) : null,
                        };
                    });
                }

                setResponsablesOptions(__responsablesOptions);
                setInitial([]);
                reset(result);

                setIncludeContract(result.includeUseContractId ? true : false);

                setValuesFiles(result.attachments);

                setMaintenanceContract(result);
            }
            else {
                const additionalData = await AdditionalDataService.getByType('MAINTENANCE_CONTRACT');
                setInitial(additionalData.map(x => {
                    return { 'additionalDataId': x.id ?? '', 'type': x.type, 'designation': x.designation };
                }));
                setReRender(x => x + 1);
            }

            Loading.hide();

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.MAINTENANCE_CONTRACTS, `Couldn't get information to create maintenance contract`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const getUseContractAssociate = async (id: string) => {
        try {
            const result = await UseContractsService.getById(id);
            reset({ ...maintenanceContract, additionalData: [...getValues().additionalData], includeUseContractId: id, cost: 0, periodicity: result.periodicity, startDate: moment(result.startDate).toDate(), endDate: result.endDate != undefined ? moment(result.endDate).toDate() : undefined, supplierId: supplierOptions.find(x => x.value == result.supplierId) ? result.supplierId : undefined });            
            setReRender(x=> x+1)
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.MAINTENANCE_CONTRACTS, `Couldn't get information to use contract associate`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const onSubmit = async (form: MaintenanceContractDto) => {
        try {
            form.vehicleId = vehicleId;
            form.invoicesToDelete = invoicesToRemove;
            form.invoices = (form.invoices ?? []).filter(x => x.isNew)
            form.triggers = (form.triggers ?? [])

            if (invoiceMode === 'add') {
                addToast(t('contracts.invoices.save_invoice_toast'), { appearance: 'warning' });
                return;
            }

            if (form.number == null || form.cost == null || !form.startDate || !form.endDate || (!form.periodicity && !includeContract)) {
                addToast(t('common.messages.complete_required_fields'), { appearance: 'warning' });
                return;
            }


            Loading.show();
            form.attachments = prepareFilesToSubmit();

            if (form.id) {
                await MaintenanceContractsService.update(form, form.attachments ?? [])
            } else {
                itemId = await MaintenanceContractsService.create(form, form.attachments ?? [])
            }

            setInvoicesToRemove([])
            getData();
            Loading.hide();
            onSubmitted();

            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
        } catch (error) {
            Loading.hide();

            if (error?.response?.status === 409) {
                addToast(t('common.messages.name_already_exists'), { appearance: 'warning' });
                return;
            }

            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.MAINTENANCE_CONTRACTS, `Couldn't update the maintenance contract with id: ${form.id}`, error);
            arrangeFilesAfterSubmit();

        }
    }

    const showRemoveItemDialog = (item: MaintenanceContractDto) => {
        setItemToRemove(item);
        setDialogDeleteItemIsOpen(true);
    };

    const removeItem = async () => {
        setDialogDeleteItemIsOpen(false);
        if (!!itemToRemove) {
            try {
                Loading.show();
                await MaintenanceContractsService.remove(itemToRemove);
                addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
                onBackList();
                Loading.hide();
            } catch (error) {
                addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
                Loading.hide();
            }
        }
    };

    const arrangeFilesAfterSubmit = () => {
        setValuesFiles(savedFiles);
    }

    const prepareFilesToSubmit = () => {
        const baseAttachments = getValues('attachments')?.filter(x => !x.id) ?? []

        const invoiceFiles: CustomFile[] = [];
        const invoicesToSave = (getValues('invoices') ?? []).filter(x => x.isNew);
        invoicesToSave.map(x => invoiceFiles.push(x.file!))

        const allAttachments = [...baseAttachments, ...invoiceFiles]
        setValuesFiles(baseAttachments);
        return allAttachments
    }

    const setValuesFiles = (files: CustomFile[]) => {
        setValue('attachments', files);
        setSavedFiles(files?.filter(x => x.id));
    }

    const onRemoveFile = (file: CustomFile) => {
        if (file.id) {
            const arr = [...(getValues('attachmentsToRemove') || []), file.id];
            setValue('attachmentsToRemove', arr);
        }
    }

    const renderButtons = () => {
        return <div className={styles.buttonsContainer}>
            <Button
                preset={'secondary'}
                type="button"
                onClick={() => {
                    if (maintenanceContract) reset(maintenanceContract);
                    onBackList();
                }}
                text={t('common.cancel')}
            />
            {isDetails && hasContractsWritePolicy &&
                <Button
                    type="button"
                    text={t('common.remove')}
                    preset={'danger'}
                    onClick={() => showRemoveItemDialog({ id: maintenanceContract?.id } as MaintenanceContractDto)} />
            }
            {isDetails && hasContractsWritePolicy && <Button
                type={'button'}
                text={t('common.edit')}
                onClick={() => onItemDetails(false)}
            />}
            {!isDetails && hasContractsWritePolicy && <Button
                type={'submit'}
                text={t('common.save')}
            />}

            <QuestionYesNo message={t('common.messages.remove_record')}
                isVisible={dialogDeleteItemIsOpen}
                onYes={() => removeItem()}
                onNo={() => setDialogDeleteItemIsOpen(false)} />
        </div>
    }

    const onAddContractInvoice = (invoice: ContractInvoiceDto) => {
        const currentInvoices = getValues('invoices') ?? [];
        setValue('invoices', [invoice, ...currentInvoices])
    };

    const onRemoveContractInvoice = (invoice: ContractInvoiceDto) => {
        if (!invoice.isNew) {
            setInvoicesToRemove(x => [...x, invoice.id!])
        }

        const currentInvoices = getValues('invoices') ?? [];
        const helper = currentInvoices.filter(x => invoice.id !== x.id)
        setValue('invoices', [...helper])
    };


    useEffect(() => {
        getData().catch(console.error);
    }, []);

    // Here the supplier is the required field and is picked first, so the direction is the
    // reverse of the maintenance screen: the types are filtered by the chosen supplier.
    const watchedSupplierId = watch('supplierId');
    const loadedTypesSupplierId = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        let cancelled = false;

        const loadMaintenanceTypes = async () => {
            try {
                const result = await MaintenanceContractTypesService.catalog(
                    watchedSupplierId ? { supplierId: watchedSupplierId } : undefined
                ) ?? [];

                if (cancelled) { return; }

                let options = Utils.sortAlphabetically(
                    result.map(x => ({ ...x, label: x.isSystem ? t(`common.maintenance_contract_type.${x.label}` as any) : x.label }))
                );

                // The effect also runs on mount (before reset fills the form), so "initial" means
                // there was no supplier yet — not just the very first run.
                const isInitialLoad = !loadedTypesSupplierId.current;
                const currentTypes: MaintenanceContractTypeItemDto[] = getValues('maintenanceContractTypes') ?? [];
                const missing = currentTypes.filter(ct => !options.find(o => o.value === ct.maintenanceContractTypeId));

                if (missing.length > 0) {
                    if (isInitialLoad) {
                        // Editing an existing contract: keep the stored types visible even if the
                        // supplier no longer declares them, so they aren't silently dropped.
                        options = Utils.sortAlphabetically([
                            ...options,
                            ...missing.map(ct => ({
                                value: ct.maintenanceContractTypeId,
                                label: ct.maintenanceContractTypeName ?? ct.name ?? '',
                            })),
                        ]);
                    } else {
                        // The user changed the supplier: drop the types the new one doesn't perform.
                        setValue('maintenanceContractTypes', currentTypes.filter(ct => options.find(o => o.value === ct.maintenanceContractTypeId)));
                    }
                }

                loadedTypesSupplierId.current = watchedSupplierId ?? null;
                setMaintenanceContractTypeOptions(options);
            } catch (error) {
                if (cancelled) { return; }
                Logger.error(LOGGER_LOG_TYPE.MAINTENANCE_CONTRACTS, `Couldn't get the maintenance types for the supplier`, error);
            }
        }

        loadMaintenanceTypes();

        return () => { cancelled = true; };
    }, [watchedSupplierId]);




    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div {...props}>
                <Row style={{ margin: 0 }}>
                    <Col xs={12} sm={12} md={12} lg={8} xl={9}>
                        <div className={styles.content}>
                            <Row style={{ alignContent: 'end' }}>
                                <Col xs={12}>
                                    <div className={styles.header}>
                                        <img src={IconBack} className={styles.icon} onClick={() => onBackList()} /> {t('contracts.maintenance_contract.title')}
                                    </div>
                                </Col>
                                <Col xs={12} sm={12} md={6} lg={6} xl={4}>
                                    <FormItem >
                                        <Label className={styles.label}>{t('contracts.maintenance_contract.include_use_contract')}</Label>
                                        <SelectController
                                            form={form}
                                            isDisabled={isDetails}
                                            placeholder={t('contracts.maintenance_contract.number')}
                                            name="includeUseContractId"
                                            isClearable={true}
                                            options={useContractOptions}
                                            onChangeSelect={(data: SelectValueLabel) => {
                                                if (!data) {
                                                    setIncludeContract(false);
                                                    reset({ ...maintenanceContract, additionalData: [...getValues().additionalData], includeUseContractId: undefined, supplierId: undefined });
                                                    setReRender(x => x+1);
                                                } else {
                                                    setIncludeContract(true);
                                                    const id = getValues('includeUseContractId')?.toString();
                                                    id && getUseContractAssociate(id);
                                                }
                                            }} />
                                    </FormItem>
                                </Col>
                                {/* The supplier is required and drives the available types, so it comes first. */}
                                <Col xs={12} sm={12} md={6} lg={6} xl={4}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('contracts.maintenance_contract.supplier')} {!isDetails ? '*' : ''}</Label>
                                        <SelectController
                                            form={form}
                                            isDisabled={isDetails}
                                            placeholder={t('contracts.maintenance_contract.supplier')}
                                            name="supplierId"
                                            options={!supplierOptions.find(x => x.value == getValues('supplierId')) && Boolean(itemId) ?
                                                [...supplierOptions, { label: getValues('supplierName'), value: getValues('supplierId') }] as SelectValueLabel[] : supplierOptions}
                                            value={isDetails && getValues('supplierId') ? { label: getValues('supplierName'), value: getValues('supplierId') } as SelectValueLabel : null}
                                            rules={{ required: true }} />
                                        <InputError error={errors.supplierId} />
                                    </FormItem>
                                </Col>
                                <Col xs={12} sm={12} md={6} lg={6} xl={4}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('common.type')}</Label>
                                        <Controller
                                            render={({ onChange, value }) => {
                                                return (
                                                    <Select
                                                        options={maintenanceContractTypeOptions}
                                                        isClearable
                                                        isMulti
                                                        isDisabled={isDetails}
                                                        placeholder={t('common.type')}
                                                        onChange={(data: SelectValueLabel[]) => {
                                                            onChange(data?.map(x => ({ maintenanceContractTypeId: x.value })));
                                                        }}
                                                        value={value ? maintenanceContractTypeOptions.filter(x => value.find((y: MaintenanceContractTypeItemDto) => y.maintenanceContractTypeId === x.value)) : []}
                                                        filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                                    />
                                                );
                                            }}
                                            control={control}
                                            name="maintenanceContractTypes"
                                            defaultValue={getValues('maintenanceContractTypes')} />
                                    </FormItem>
                                </Col>
                                <Col xs={12} sm={12} md={6} lg={6} xl={4}>
                                    <FormItem >
                                        <Label className={styles.label}>{t('contracts.maintenance_contract.number')} {!isDetails ? '*' : ''}</Label>
                                        <InputController
                                            name="number"
                                            form={form as any}
                                            autoComplete='off'
                                            disabled={isDetails}
                                            placeholder={t('contracts.maintenance_contract.number')}
                                            rules={{ ...DEFAULT_INPUT_RULES_WITH_REQUIRED }} />
                                        <InputError error={errors.number} />
                                    </FormItem>
                                </Col>

                                {!includeContract && <Col xs={12} sm={12} md={6} lg={6} xl={4}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('common.periodicity')} {!isDetails ? '*' : ''}</Label>
                                        <SelectController
                                            form={form}
                                            isDisabled={isDetails}
                                            placeholder={t('common.periodicity')}
                                            name="periodicity"
                                            options={periodicityOptions}
                                            rules={{ required: true }}
                                            onChangeSelect={(data: SelectValueLabel) => {
                                                setSelectedPeriodicity(data ? data.label : null);
                                            }} />
                                        <InputError error={errors.periodicity} />
                                    </FormItem>
                                </Col>}
                                {!includeContract && <Col xs={12} sm={12} md={6} lg={6} xl={4}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('contracts.maintenance_contract.cost')} {!isDetails ? '*' : ''}</Label>
                                        <InputGroupController
                                            type="number"
                                            text={selectPeriodicity ? t('common.euro') + '/' + selectPeriodicity : t('common.euro')}
                                            placeholder={t('contracts.maintenance_contract.cost')}
                                            name="cost"
                                            form={form}
                                            disabled={isDetails || includeContract}
                                            format="money"
                                            rules={{ required: true }} />
                                        <InputError error={errors.cost} />
                                        {includeContract && <span className={styles.messageAlert}>{'*' + t('contracts.maintenance_contract.included_maintenance_contract_in_cost')}</span>}
                                    </FormItem>
                                </Col>}
                                <Col xs={12} sm={12} md={6} lg={6} xl={4}>
                                    <FormItem>
                                        <Label className={styles.label}>{t('common.start_end_date')} {!isDetails ? '*' : ''}</Label>
                                        <div className={styles.dateRangeCustom}>
                                            <DateTimePickerRange
                                                dateFormat="dd/MM/yyyy"
                                                locale={locale}
                                                start={watch('startDate')}
                                                end={watch('endDate')}
                                                showYearDropdown
                                                key={`watch-${watch('startDate')}-${watch('endDate')}`}
                                                disabled={isDetails}
                                                placeholderText={t('common.start_end_date')}
                                                onChange={() => {
                                                    // do nothing.
                                                }}
                                                onChangeRange={(start: Date | null, end: Date | null) => {
                                                    const sd = start
                                                        ? Utils.generateDate(start.getFullYear(), start.getMonth(), start.getDate())
                                                        : null;
                                                    setValue('startDate', sd);

                                                    let dateLastDayOfMonth = end;
                                                    if (end) {
                                                        dateLastDayOfMonth = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59);
                                                    }
                                                    setValue('endDate', dateLastDayOfMonth);
                                                }}
                                            />
                                        </div>
                                        <InputError error={form.formState.isSubmitted && (!watch('startDate') || !watch('endDate')) ? { type: 'dateRange', message: t('common.errors.date_not_filled') } : undefined} />
                                    </FormItem>
                                </Col>

                                 <AdditionalDataForm form={form}
                                    key={`additional_data_form_${reRender}`}
                                    xs={12} sm={12} md={6} lg={6} xl={4}
                                    initial={initial}
                                    isDetails={isDetails}
                                />
                            </Row>
                            <Row>
                                <ReminderTriggerForm form={form}
                                    key={`ReminderTriggerForm_${maintenanceContract?.id || 'new'}`}
                                    control={1}
                                    title={t('reminders.notifications.title_contract_end') as string}
                                    designation={t('maintenances.notices_over_contract_end_date')}
                                    responsablesOptions={responsablesOptions}
                                    measureOptions={measureOptions}
                                    criteriaOptions={criteriaOptions}
                                    isDetails={isDetails}
                                    onCreateResponsableOption={(data: any) => { setResponsablesOptions([...responsablesOptions, { value: data, label: data }]) }}
                                    defaultFirstDate={form.watch('endDate')}
                                />
                            </Row>
                            {renderButtons()}
                        </div>
                    </Col>
                    <Col xs={12} sm={12} md={12} lg={4} xl={3}
                        className={styles.colRightContent}>
                        <Label className={styles.subtitle}>{t('common.attachment')}</Label>
                        <FormItem>
                            <FileSelector
                                isDetails={isDetails}
                                isMulti={true}
                                smallButtonAdd={true}
                                initialFiles={getValues('attachments')}
                                onFilesChanged={setValuesFiles}
                                onRemoveFile={onRemoveFile}
                                label={<Label className={styles.label}>{t('contracts.maintenance_contract.title')}</Label>}
                                fileButtonSizes={{ sm: 12, lg: 6 }}
                            />
                        </FormItem>

                        <Separator />
                        <ContractInvoices
                            mode={invoiceMode}
                            setMode={mode => setInvoiceMode(mode)}
                            invoices={form.watch('invoices') ?? []}
                            onAddInvoice={onAddContractInvoice}
                            onRemoveInvoice={onRemoveContractInvoice}
                            isDetails={isDetails}
                        />
                    </Col>
                </Row>
            </div>
        </form>
    );
}

export default MaintenanceContractsScreen;
